/**
 * Drops fields the user left blank from a form payload.
 *
 * Formik initialises text inputs to `''`, so an untouched optional field would
 * otherwise be sent as an empty string and stored as one. Removing the key lets
 * the API apply its own default, or leave the field out entirely.
 *
 * Only `undefined`, `null` and blank strings count as empty — `false` and `0` are
 * real values a checkbox or a number input can produce, and are always kept.
 *
 * The pass is shallow on purpose: nested objects a handler assembles itself
 * (`documentation: { url, name }`) carry their own meaning and are left intact.
 *
 * Note this removes a field rather than clearing it. On an update endpoint where
 * `null` means "clear this value", pass that `null` explicitly instead of relying
 * on this helper.
 */
export const isEmptyFormValue = (value: unknown): boolean =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

export const stripEmptyFields = <T extends object>(values: T): Partial<T> => {
    const result: Partial<T> = {};

    (Object.keys(values) as (keyof T)[]).forEach((key) => {
        if (!isEmptyFormValue(values[key])) {
            result[key] = values[key];
        }
    });

    return result;
};
